import {
  buildSubmissionDocument,
  type Answers,
  type FormSchema,
  type Lang,
} from '../logic/form-logic';
import type { FormCopy } from './copy';
import { FieldError, FormButton } from './ui';

/**
 * "Check your answers": every question the patient saw, what they said, and a
 * Change button for each (SFM-D1). Built from the same rows staff read, so the
 * patient checks exactly what the pharmacy will see.
 */
export function ReviewList({
  idPrefix,
  schema,
  answers,
  visible,
  lang,
  copy,
  errors,
  onChange,
}: {
  idPrefix: string;
  schema: FormSchema;
  answers: Record<string, unknown>;
  visible: string[];
  /** The form's language: the rows. `copy` may be the page's (SFM-D33). */
  lang: Lang;
  copy: FormCopy;
  /** Messages by question id, already in words. */
  errors: Record<string, string>;
  onChange: (questionId: string) => void;
}) {
  const rows = buildSubmissionDocument(schema, answers as Answers, lang, visible);
  return (
    <dl className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
      {rows.map((row) => {
        const error = errors[row.questionId];
        const errorId = `${idPrefix}-${row.questionId}-review-error`;
        return (
          <div key={row.questionId} className="p-4">
            <dt lang={lang} className="text-base font-medium text-muted-foreground">
              {row.label}
            </dt>
            <dd className="mt-1 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
              <span lang={lang} className="min-w-0 flex-1 whitespace-pre-line break-words text-lg text-foreground">
                {row.signature?.kind === 'drawn' ? (
                  <img
                    src={`data:image/png;base64,${row.signature.png}`}
                    alt={copy.yourSignature}
                    className="h-16 max-w-full rounded border border-border bg-card"
                  />
                ) : row.answered ? (
                  row.answer
                ) : (
                  <span className="text-muted-foreground">{copy.notAnswered}</span>
                )}
                {error && <FieldError id={errorId}>{error}</FieldError>}
              </span>
              <FormButton
                variant="link"
                aria-label={copy.changeAnswer(row.label)}
                aria-describedby={error ? errorId : undefined}
                onClick={() => onChange(row.questionId)}
              >
                {copy.change}
              </FormButton>
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
